import { BareResponse } from "@mercuryworkshop/proxy-transports";
import {
	BodyType,
	ScramjetFetchHandler,
	ScramjetFetchParsed,
	ScramjetFetchRequest,
} from ".";
import {
	flagEnabled,
	isHtmlMimeType,
	isJavascriptMimeType,
	rewriteCss,
	rewriteHtml,
	rewriteJs,
	rewriteWorkers,
} from "@/shared";
import { sniffEncoding } from "@/shared/sniffEncoding";
import { _TextDecoder } from "@/shared/snapshot";

// A page sent without a usable Content-Type is sniffed by the browser, which shows it as HTML
// when it starts like HTML. It came through unrewritten (an ad frame on temp-mail.org sent its
// sync pictures that way, and they loaded straight from the sites). Documents are sniffed the
// same way here (the MIME Sniffing standard's HTML patterns), and the answer says text/html.
export const sniffedHtml = new WeakSet<object>();

function needsSniff(contentType: string): boolean {
	const essence = contentType.split(";")[0].trim().toLowerCase();
	if (!essence) return true;
	if (essence === "unknown/unknown" || essence === "application/unknown" || essence === "*/*")
		return true;
	// an unparsable type counts as none (Forter's "Content-Type: UTF-8")
	return !/^[!#$%&'*+.^_`|~0-9a-z-]+\/[!#$%&'*+.^_`|~0-9a-z-]+$/.test(essence);
}

const HTML_STARTS = [
	"<!doctype html", "<html", "<head", "<script", "<iframe", "<h1", "<div", "<font",
	"<table", "<a", "<style", "<title", "<b", "<body", "<br", "<p",
];
function looksLikeHtml(bytes: Uint8Array): boolean {
	let i = 0;
	if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) i = 3;
	while (i < bytes.length && (bytes[i] === 0x09 || bytes[i] === 0x0a || bytes[i] === 0x0c || bytes[i] === 0x0d || bytes[i] === 0x20)) i++;
	let head = "";
	for (let j = i; j < bytes.length && j < i + 16; j++) head += globalThis.String.fromCharCode(bytes[j]);
	head = head.toLowerCase();
	if (head.startsWith("<!--")) return true;
	for (const start of HTML_STARTS) {
		if (!head.startsWith(start)) continue;
		const next = head.charCodeAt(start.length);
		if (next === 0x20 || next === 0x3e) return true;
	}

	return false;
}

export async function rewriteBody(
	handler: ScramjetFetchHandler,
	request: ScramjetFetchRequest,
	parsed: ScramjetFetchParsed,
	response: BareResponse
): Promise<BodyType> {
	switch (parsed.destination) {
		case "iframe":
		case "document": {
			const contentType = response.headers.get("content-type") ?? "";
			let sniffed: Uint8Array | null = null;
			if (!isHtmlMimeType(contentType) && needsSniff(contentType)) {
				sniffed = new Uint8Array(await response.arrayBuffer());
				if (!looksLikeHtml(sniffed)) return sniffed.buffer as ArrayBuffer;
				sniffedHtml.add(parsed);
			}
			if (sniffed || isHtmlMimeType(contentType)) {
				const bytes = sniffed ?? new Uint8Array(await response.arrayBuffer());
				const encoding = sniffEncoding(
					bytes,
					response.headers.get("content-type")
				);
				const htmlContent = new _TextDecoder(encoding).decode(bytes);

				return rewriteHtml(htmlContent, handler.context, parsed.meta, {
					loadScripts: true,
					inline: true,
					source: parsed.url.href,
					headers: response.rawHeaders,
					// reasonably confident that a document fetch is impossible without a client
					history: parsed.trackedClient!.history,
				});
			} else {
				return response.body;
			}
		}
		case "script": {
			// do not attempt to rewrite a 404 response
			if (response.ok) {
				const ct = response.headers.get("content-type");
				// don't rewrite invalid module scripts when the server declares a non-JS type
				if (parsed.isModule && ct && !isJavascriptMimeType(ct)) {
					return response.body;
				}

				let rewritten = rewriteJs(
					new Uint8Array(await response.arrayBuffer()),
					response.url,
					handler.context,
					parsed.meta,
					parsed.isModule
				);

				if (
					flagEnabled("debugSourceURL", handler.context, parsed.meta.origin)
				) {
					if (rewritten instanceof Uint8Array) {
						rewritten = new TextDecoder().decode(rewritten);
					}
					rewritten += `\n//# sourceURL=${parsed.url.href}`;
				}

				return rewritten as unknown as ArrayBuffer;
			}
			return response.body;
		}
		case "style":
			return rewriteCss(await response.text(), handler.context, parsed.meta);
		case "sharedworker":
		case "worker":
			return rewriteWorkers(
				new Uint8Array(await response.arrayBuffer()),
				response.url,
				handler.context,
				parsed.meta,
				parsed.isModule
			);
		default:
			return response.body;
	}
}
